import {
  ReactElement,
  ChangeEvent,
  SyntheticEvent,
  HTMLAttributes,
  useCallback,
  useRef,
  useMemo,
  useState,
} from 'react';
import {
  TextFieldProps,
  AutocompleteRenderGetTagProps,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  Stack,
  DialogContentText,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVectorSquare as LogisticsZonesIcon } from '@fortawesome/free-solid-svg-icons';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { get } from 'lodash';

import {
  AccessFeature,
  DialogConfimation,
  LogisiticZoneInfo,
} from '~components/index';
import { CustomAutocompleteRef } from '~components/CustomAutocomplete/types';
import { TimeWindowPickerRef } from '~components/TimeWindowPicker/types';
import { AccessFeatureCommonProps } from '~components/AccessFeature/types';
import {
  useLazyRequest,
  useSelectedRows,
  useToggle,
  useSubscriptionFeatures,
} from '~hooks/index';
import { editMultipleItems } from '~services/item';
import { LogisticZoneBase } from '~services/logisticsZones/types';
import {
  SelectRowsHandlerOnSelectAll,
  SelectRowsHandlerOnSelect,
} from '~hooks/useDataTableSelectedProps';
import { DynamicDataTable, Nullable } from '~globals/types';
import { PlanFeaturesTypes } from '~globals/types/enums';
import { hasError } from '~utils/formHelpers';

import DialogOrdersMultipeEditionTable from '../Table';
import DialogOrdersMultipeEditionPreviewModifications from '../PreviewModifications';

import { DialogOrdersMultipeEditionContentProps } from '../types';
import {
  FIELDS_NAME,
  EXTRACTOR_KEY_VALUE,
  initialValues,
  validationSchema,
  validateRowsChecked,
  getParseVisibleValues,
} from '../utils';
import {
  DialogOrdersMultipeEditionActions,
  DialogOrdersMultipeEditionChip,
  DialogOrderEmailsChip,
  DialogOrderEmailsChipWrapper,
} from '../styles';

const DialogOrdersMultipeEditionContent = ({
  onClose,
  selectedIds,
  skillsNeededOptions,
  serviceTypesOptions,
  zoneOptions,
  onSuccessSubmit,
}: DialogOrdersMultipeEditionContentProps): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();
  const { hasAccessToFeature } = useSubscriptionFeatures();

  const skillsNeededAutocompleteRef =
    useRef<CustomAutocompleteRef<string, true>>(null);

  const timeWindowPickerRef = useRef<TimeWindowPickerRef>(null);

  const serviceTypeListAutocompleteRef =
    useRef<CustomAutocompleteRef<string, false>>(null);

  const zoneIdAutocompleteRef =
    useRef<CustomAutocompleteRef<LogisticZoneBase, false>>(null);

  const [openConfirmModifidyOrders, toggleOpenConfirmModifidyOrders] =
    useToggle();

  const [emailValue, setEmailValue] = useState<string>('');

  const {
    touched,
    errors,
    values,
    setFieldValue,
    setFieldTouched,
    getFieldProps,
    submitForm,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: () => toggleOpenConfirmModifidyOrders(true),
  });

  const handleChangeTimeWindowAllDay = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;

      setFieldValue(FIELDS_NAME.TIME_WINDOW_ALL_DAY, checked);
      setFieldValue(FIELDS_NAME.TIME_WINDOW_ONE_FROM, null);
      setFieldValue(FIELDS_NAME.TIME_WINDOW_ONE_TO, null);
      setFieldValue(FIELDS_NAME.TIME_WINDOW_SECOND_FROM, null);
      setFieldValue(FIELDS_NAME.TIME_WINDOW_SECOND_TO, null);

      if (checked) {
        timeWindowPickerRef.current?.handleDeleteSecondTimeWindow();
      }
    },
    [setFieldValue],
  );

  const handleClearTimeWindowTwo = useCallback(() => {
    setFieldValue(FIELDS_NAME.TIME_WINDOW_SECOND_FROM, null);
    setFieldValue(FIELDS_NAME.TIME_WINDOW_SECOND_TO, null);
  }, [setFieldValue]);

  const handleChangeDate = useCallback(
    (fieldName: string) =>
      (value: Date | null): void => {
        setFieldValue(fieldName, value);
      },
    [setFieldValue],
  );

  const handleBlurDate = useCallback(
    (fieldName: string) => (): void => {
      setFieldTouched(fieldName, true);
    },
    [setFieldTouched],
  );

  const renderInputPicker = useCallback(
    (props: TextFieldProps, nameField: string) => (
      <TextField
        {...props}
        autoComplete="off"
        fullWidth
        onBlur={handleBlurDate(nameField)}
        error={hasError(touched, errors, nameField)}
      />
    ),
    [handleBlurDate, touched, errors],
  );

  const handleChangeSkills = useCallback(
    (_e: SyntheticEvent<Element, Event>, newVals: string[]) => {
      setFieldValue(FIELDS_NAME.DELIVERY_SKILLS_NEEDED, newVals);
    },
    [setFieldValue],
  );

  const handleBlurSkills = useCallback(() => {
    setFieldTouched(FIELDS_NAME.DELIVERY_SKILLS_NEEDED, true);
  }, [setFieldTouched]);

  const renderTagsSkills = useCallback(
    (vals: string[], getTagsProps: AutocompleteRenderGetTagProps) =>
      vals.map((val, index) => (
        // eslint-disable-next-line react/jsx-key
        <DialogOrdersMultipeEditionChip
          label={val}
          size="small"
          {...getTagsProps({ index })}
        />
      )),
    [],
  );

  const handleChangeServiceType = useCallback(
    (_e: SyntheticEvent<Element, Event>, newVal: string | null) => {
      setFieldValue(FIELDS_NAME.SERVICE_TYPE, newVal);
    },
    [setFieldValue],
  );

  const handleBlurServiceType = useCallback(() => {
    setFieldTouched(FIELDS_NAME.SERVICE_TYPE, true);
  }, [setFieldTouched]);

  const handleChangeZoneId = useCallback(
    (_e: SyntheticEvent<Element, Event>, newVal: LogisticZoneBase | null) => {
      setFieldValue(FIELDS_NAME.ZONE_ID, newVal?.zoneId ?? null);
    },
    [setFieldValue],
  );

  const handleBlurZoneId = useCallback(() => {
    setFieldTouched(FIELDS_NAME.ZONE_ID, true);
  }, [setFieldTouched]);

  const rowsCheckedValue = useMemo(
    () => get(values, FIELDS_NAME.ROWS_CHECKED),
    [values],
  );

  const getDisabledRow = useCallback(
    (rowFieldName: string) =>
      !validateRowsChecked(rowsCheckedValue, rowFieldName),
    [rowsCheckedValue],
  );

  const isDisabledTimeWindowDates = useMemo(
    () =>
      getDisabledRow(FIELDS_NAME.TIME_WINDOW) ||
      get(values, FIELDS_NAME.TIME_WINDOW_ALL_DAY),
    [getDisabledRow, values],
  );

  const handleNewTag = useCallback(
    (tag: string) => {
      const newTag = tag.trim();
      if (newTag !== '') {
        const tagsArr = values[FIELDS_NAME.SELLER_MAIL]
          ? values[FIELDS_NAME.SELLER_MAIL].split(',').filter(Boolean)
          : [];
        tagsArr.push(tag);
        setEmailValue('');
        setFieldValue(FIELDS_NAME.SELLER_MAIL, tagsArr.join(','));
        setFieldTouched(FIELDS_NAME.SELLER_MAIL, true);
      }
    },
    [values, setFieldValue, setFieldTouched],
  );

  const handleAddEmailTag = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        handleNewTag(emailValue);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [emailValue],
  );

  const handleDeleteEmailTag = useCallback(
    (tagIndex: number, tags: string[]) => {
      const updatedTags = tags.filter((_, index) => index !== tagIndex);
      setFieldValue(FIELDS_NAME.SELLER_MAIL, updatedTags.join(','));
    },
    [setFieldValue],
  );

  const handleChangeEmailText = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setEmailValue(e.target.value),
    [setEmailValue],
  );

  const handleEmailTextBlur = useCallback(() => {
    handleNewTag(emailValue);
    setFieldTouched(FIELDS_NAME.SELLER_MAIL, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailValue, setFieldTouched]);

  const renderEmailTags = useCallback((vals: string[]) => {
    const filteredVals = vals.filter((val) => val.length);
    if (!filteredVals.length) return null;
    return (
      <DialogOrderEmailsChipWrapper direction={'row'}>
        {filteredVals.map((val, index) => (
          <DialogOrderEmailsChip
            key={`wrapper-tag-${index}`}
            label={val}
            size="small"
            onDelete={() => handleDeleteEmailTag(index, filteredVals)}
          />
        ))}
      </DialogOrderEmailsChipWrapper>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const data = useMemo<DynamicDataTable[]>(
    () => [
      {
        id: FIELDS_NAME.CLIENT_NAME,
        component: 'Input',
        label: 'Nombre de contacto',
        componentProps: {
          label: 'Ingrese el valor',
          fullWidth: true,
          autoComplete: 'off',
          error: hasError(touched, errors, FIELDS_NAME.CLIENT_NAME),
          disabled: getDisabledRow(FIELDS_NAME.CLIENT_NAME),
          ...getFieldProps(FIELDS_NAME.CLIENT_NAME),
        },
      },
      {
        id: FIELDS_NAME.ADDRESS_WAIT_TIME,
        label: 'Tiempo estimado en parada (minutos)',
        component: 'Input',
        componentProps: {
          label: 'Ingrese el valor',
          fullWidth: true,
          autoComplete: 'off',
          type: 'number',
          error: hasError(touched, errors, FIELDS_NAME.ADDRESS_WAIT_TIME),
          disabled: getDisabledRow(FIELDS_NAME.ADDRESS_WAIT_TIME),
          ...getFieldProps(FIELDS_NAME.ADDRESS_WAIT_TIME),
        },
      },
      {
        id: FIELDS_NAME.TIME_WINDOW,
        label: 'Ventana horaria de visita',
        component: 'TimeWindowPicker',
        componentProps: {
          ref: timeWindowPickerRef,
          windowAllDayProps: {
            ...getFieldProps(FIELDS_NAME.TIME_WINDOW_ALL_DAY),
            checked: get(values, FIELDS_NAME.TIME_WINDOW_ALL_DAY),
            onChange: handleChangeTimeWindowAllDay,
            disabled: getDisabledRow(FIELDS_NAME.TIME_WINDOW),
          },
          windowOneProps: {
            fromProps: {
              value: get(values, FIELDS_NAME.TIME_WINDOW_ONE_FROM),
              onChange: handleChangeDate(FIELDS_NAME.TIME_WINDOW_ONE_FROM),
              disabled: isDisabledTimeWindowDates,
              renderInput: (props: TextFieldProps) =>
                renderInputPicker(props, FIELDS_NAME.TIME_WINDOW_ONE_FROM),
            },
            toProps: {
              value: get(values, FIELDS_NAME.TIME_WINDOW_ONE_TO),
              onChange: handleChangeDate(FIELDS_NAME.TIME_WINDOW_ONE_TO),
              disabled: isDisabledTimeWindowDates,
              renderInput: (props: TextFieldProps) =>
                renderInputPicker(props, FIELDS_NAME.TIME_WINDOW_ONE_TO),
            },
          },
          windowTwoProps: {
            fromProps: {
              value: get(values, FIELDS_NAME.TIME_WINDOW_SECOND_FROM),
              onChange: handleChangeDate(FIELDS_NAME.TIME_WINDOW_SECOND_FROM),
              disabled: isDisabledTimeWindowDates,
              renderInput: (props: TextFieldProps) =>
                renderInputPicker(props, FIELDS_NAME.TIME_WINDOW_SECOND_FROM),
            },
            toProps: {
              value: get(values, FIELDS_NAME.TIME_WINDOW_SECOND_TO),
              onChange: handleChangeDate(FIELDS_NAME.TIME_WINDOW_SECOND_TO),
              disabled: isDisabledTimeWindowDates,
              renderInput: (props: TextFieldProps) =>
                renderInputPicker(props, FIELDS_NAME.TIME_WINDOW_SECOND_TO),
            },
          },
          onClearWindowTwoProps: handleClearTimeWindowTwo,
          disabledActions: isDisabledTimeWindowDates,
        },
      },
      {
        id: FIELDS_NAME.DELIVERY_EXPIRATION_DATE,
        label: 'Fecha de vencimiento (Opcional)',
        component: 'DatePicker',
        componentProps: {
          label: 'Ingrese la fecha',
          value: get(values, FIELDS_NAME.DELIVERY_EXPIRATION_DATE),
          onChange: handleChangeDate(FIELDS_NAME.DELIVERY_EXPIRATION_DATE),
          disabled: getDisabledRow(FIELDS_NAME.DELIVERY_EXPIRATION_DATE),
          disablePast: true,
          renderInput: (props: TextFieldProps) =>
            renderInputPicker(props, FIELDS_NAME.DELIVERY_EXPIRATION_DATE),
        },
      },
      {
        id: FIELDS_NAME.DELIVERY_TAGS,
        component: 'Input',
        label: 'Grupo (Opcional)',
        componentProps: {
          label: 'Ingrese el valor',
          fullWidth: true,
          autoComplete: 'off',
          error: hasError(touched, errors, FIELDS_NAME.DELIVERY_TAGS),
          disabled: getDisabledRow(FIELDS_NAME.DELIVERY_TAGS),
          ...getFieldProps(FIELDS_NAME.DELIVERY_TAGS),
        },
      },
      {
        id: FIELDS_NAME.DELIVERY_SKILLS_NEEDED,
        label: 'Cargas especiales o atributos requeridos (Opcional)',
        component: 'Autocomplete',
        componentProps: {
          innerRef: skillsNeededAutocompleteRef,
          multiple: true,
          disableCloseOnSelect: true,
          filterSelectedOptions: true,
          renderTags: renderTagsSkills,
          disableClearable: true,
          options: skillsNeededOptions,
          label: 'Seleccione todas las opciones posibles',
          name: FIELDS_NAME.DELIVERY_SKILLS_NEEDED,
          onChange: handleChangeSkills,
          onBlur: handleBlurSkills,
          error: hasError(touched, errors, FIELDS_NAME.DELIVERY_SKILLS_NEEDED),
          fullWidth: true,
          getOptionLabel: (option: string) => option,
          disabled: getDisabledRow(FIELDS_NAME.DELIVERY_SKILLS_NEEDED),
        },
      },
      {
        id: FIELDS_NAME.CUSTOM_FIELD_1,
        component: 'Input',
        label: 'Campo personalizado 1 (Opcional)',
        componentProps: {
          label: 'Ingrese el valor',
          fullWidth: true,
          autoComplete: 'off',
          error: hasError(touched, errors, FIELDS_NAME.CUSTOM_FIELD_1),
          disabled: getDisabledRow(FIELDS_NAME.CUSTOM_FIELD_1),
          ...getFieldProps(FIELDS_NAME.CUSTOM_FIELD_1),
        },
      },
      {
        id: FIELDS_NAME.CUSTOM_FIELD_2,
        component: 'Input',
        label: 'Campo personalizado 2 (Opcional)',
        componentProps: {
          label: 'Ingrese el valor',
          fullWidth: true,
          autoComplete: 'off',
          error: hasError(touched, errors, FIELDS_NAME.CUSTOM_FIELD_2),
          disabled: getDisabledRow(FIELDS_NAME.CUSTOM_FIELD_2),
          ...getFieldProps(FIELDS_NAME.CUSTOM_FIELD_2),
        },
      },
      {
        id: FIELDS_NAME.CUSTOM_FIELD_3,
        component: 'Input',
        label: 'Campo personalizado 3 (Opcional)',
        componentProps: {
          label: 'Ingrese el valor',
          fullWidth: true,
          autoComplete: 'off',
          error: hasError(touched, errors, FIELDS_NAME.CUSTOM_FIELD_3),
          disabled: getDisabledRow(FIELDS_NAME.CUSTOM_FIELD_3),
          ...getFieldProps(FIELDS_NAME.CUSTOM_FIELD_3),
        },
      },
      {
        id: FIELDS_NAME.SELLER_MAIL,
        component: 'Input',
        label: 'Emails para notificaciones del estado de la orden (Opcional)',
        componentProps: {
          label: 'Ingrese el valor',
          fullWidth: true,
          autoComplete: 'off',
          error: hasError(touched, errors, FIELDS_NAME.SELLER_MAIL),
          disabled: getDisabledRow(FIELDS_NAME.SELLER_MAIL),
          value: emailValue,
          onChange: handleChangeEmailText,
          onKeyDown: handleAddEmailTag,
          onBlur: handleEmailTextBlur,
          InputProps: {
            startAdornment: renderEmailTags(
              values[FIELDS_NAME.SELLER_MAIL].split(','),
            ),
            style: {
              alignItems: 'flex-start',
            },
          },
        },
      },
      {
        id: FIELDS_NAME.STORE,
        component: 'Input',
        label: 'Tienda (Opcional)',
        componentProps: {
          label: 'Ingrese el valor',
          fullWidth: true,
          autoComplete: 'off',
          error: hasError(touched, errors, FIELDS_NAME.STORE),
          disabled: getDisabledRow(FIELDS_NAME.STORE),
          ...getFieldProps(FIELDS_NAME.STORE),
        },
      },
      {
        id: FIELDS_NAME.SERVICE_TYPE,
        label: 'Tipo de servicio (Opcional)',
        component: 'AccessFeatureAutoComplete',
        componentProps: {
          validFeature: PlanFeaturesTypes.LimitNotPro,
          innerRef: serviceTypeListAutocompleteRef,
          options: serviceTypesOptions,
          label: 'Seleccione la opción',
          name: FIELDS_NAME.SERVICE_TYPE,
          onChange: handleChangeServiceType,
          onBlur: handleBlurServiceType,
          error: hasError(touched, errors, FIELDS_NAME.SERVICE_TYPE),
          fullWidth: true,
          getOptionLabel: (option: string) => option,
          disabled: getDisabledRow(FIELDS_NAME.SERVICE_TYPE),
        },
      },
      {
        id: FIELDS_NAME.ZONE_ID,
        label: 'Zona logística (Opcional)',
        component: 'AccessFeatureAutoComplete',
        componentProps: {
          validFeature: PlanFeaturesTypes.MgmZones,
          innerRef: zoneIdAutocompleteRef,
          options: zoneOptions,
          label: 'Seleccione la opción',
          name: FIELDS_NAME.ZONE_ID,
          onChange: handleChangeZoneId,
          onBlur: handleBlurZoneId,
          error: hasError(touched, errors, FIELDS_NAME.ZONE_ID),
          fullWidth: true,
          getOptionLabel: (option: LogisticZoneBase) =>
            option.description
              ? `${option.code} ${option.description}`
              : option.code,
          renderOption: (
            props: HTMLAttributes<HTMLLIElement>,
            option: LogisticZoneBase,
          ) => (
            <li
              {...props}
              style={{ display: 'flex', justifyContent: 'space-between' }}
            >
              <LogisiticZoneInfo
                name={option.code}
                description={option.description}
                color={option.color}
                withTooltip
              />
            </li>
          ),
          startAdornment: (val: Nullable<LogisticZoneBase>) =>
            val && (
              <FontAwesomeIcon icon={LogisticsZonesIcon} color={val.color} />
            ),
          disabled: getDisabledRow(FIELDS_NAME.ZONE_ID),
        },
      },
    ],
    [
      touched,
      errors,
      getDisabledRow,
      getFieldProps,
      values,
      handleChangeTimeWindowAllDay,
      handleChangeDate,
      isDisabledTimeWindowDates,
      handleClearTimeWindowTwo,
      renderTagsSkills,
      skillsNeededOptions,
      handleChangeSkills,
      handleBlurSkills,
      emailValue,
      handleChangeEmailText,
      handleAddEmailTag,
      handleEmailTextBlur,
      renderEmailTags,
      renderInputPicker,
      serviceTypesOptions,
      handleChangeServiceType,
      handleBlurServiceType,
      zoneOptions,
      handleChangeZoneId,
      handleBlurZoneId,
    ],
  );

  const selectableRows = useSelectedRows<DynamicDataTable>(
    EXTRACTOR_KEY_VALUE,
    [],
    [],
  );

  const nonSelectableIds = useMemo(() => {
    const componentsAccessFeature = data.filter((itemData) =>
      itemData.component.includes('AccessFeature'),
    );

    if (componentsAccessFeature.length === 0) return undefined;

    const componentsNotValidFeature = componentsAccessFeature.filter(
      (itemData) => {
        const currentValidFeature:
          | AccessFeatureCommonProps['validFeature']
          | undefined = itemData.componentProps?.validFeature;
        const currentAvilableToDate: AccessFeatureCommonProps['availableToDate'] =
          itemData.componentProps?.availableToDate;

        if (!currentValidFeature) return false;

        return !hasAccessToFeature(currentValidFeature, currentAvilableToDate);
      },
    );

    if (componentsNotValidFeature.length === 0) return undefined;

    const currentIds = componentsNotValidFeature.map((itemData) => itemData.id);

    return currentIds;
  }, [data, hasAccessToFeature]);

  const onSelectAll = useCallback<
    SelectRowsHandlerOnSelectAll<DynamicDataTable>
  >(
    async (isSelected, rows, event) => {
      let rowsCheckedIds: string[] = [];

      if (isSelected) {
        const currentSelectedIds = rows.map((row) => row[EXTRACTOR_KEY_VALUE]);

        rowsCheckedIds = rowsCheckedValue.concat(currentSelectedIds);
      }

      setFieldValue(FIELDS_NAME.ROWS_CHECKED, rowsCheckedIds);

      selectableRows.handleOnSelectAll(
        isSelected,
        rows,
        event as SyntheticEvent,
      );
    },
    [setFieldValue, selectableRows, rowsCheckedValue],
  );

  const onSelect = useCallback<SelectRowsHandlerOnSelect<DynamicDataTable>>(
    (row, isSelected, rowIndex, event) => {
      const currentCheckedId = row[EXTRACTOR_KEY_VALUE];

      let rowsCheckedIds = rowsCheckedValue.filter(
        (currentId) => currentId !== currentCheckedId,
      );

      if (isSelected) {
        rowsCheckedIds = rowsCheckedValue.concat(currentCheckedId);
      }

      setFieldValue(FIELDS_NAME.ROWS_CHECKED, rowsCheckedIds);

      selectableRows.handleOnSelect(
        row,
        isSelected,
        rowIndex,
        event as SyntheticEvent,
      );
    },
    [rowsCheckedValue, selectableRows, setFieldValue],
  );

  const [, loadingModifyOrders, , executeModifyOrders] = useLazyRequest({
    request: editMultipleItems,
    withPostSuccess: () => {
      enqueueSnackbar('Pedidos modificados correctamente', {
        variant: 'success',
      });

      toggleOpenConfirmModifidyOrders(false);
      onClose();

      onSuccessSubmit?.();
    },
    withPostFailure: (err) => {
      let errorMessage =
        'No se pudieron modificar los pedidos, intenté nuevamente';

      if (err?.data?.data.message) {
        errorMessage = err.data.data.message;
      }

      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });

  const handleModifyOrders = useCallback(async () => {
    const visiblesValues = getParseVisibleValues(values);

    await executeModifyOrders({
      itemIds: selectedIds,
      ...visiblesValues,
    });
  }, [executeModifyOrders, selectedIds, values]);

  return (
    <>
      <DialogTitle>
        Edición múltiple de pedidos{' '}
        <AccessFeature.NextPlanChip
          validFeature={PlanFeaturesTypes.LimitNotPro}
          openAccessFeatureModalOnClick={false}
        />
      </DialogTitle>

      <DialogContent dividers>
        <AccessFeature.Alert validFeature={PlanFeaturesTypes.LimitNotPro} />

        <AccessFeature.Hidden
          validFeature={PlanFeaturesTypes.LimitNotPro}
          type="notAccess"
        >
          <Stack spacing={2}>
            <DialogContentText color="text.secondary">
              Seleccione los campos que desea modificar y establezca los nuevos
              valores. Los campos opcionales pueden dejarse en blanco para
              eliminar su contenido.
            </DialogContentText>

            <DialogOrdersMultipeEditionTable
              data={data}
              selectableRows={{
                selectedIds: selectableRows.selectedRowsIds,
                nonSelectableIds,
                onSelectAll,
                onSelect,
              }}
            />
          </Stack>
        </AccessFeature.Hidden>
      </DialogContent>

      <DialogOrdersMultipeEditionActions>
        <Button color="secondary" onClick={onClose}>
          Cerrar
        </Button>

        <AccessFeature.Hidden
          validFeature={PlanFeaturesTypes.LimitNotPro}
          type="notAccess"
        >
          <Button
            variant="contained"
            color="primary"
            onClick={submitForm}
            disabled={selectableRows.selectedRowsIds.length === 0}
          >
            Guardar cambios
          </Button>
        </AccessFeature.Hidden>
      </DialogOrdersMultipeEditionActions>

      <DialogConfimation
        open={openConfirmModifidyOrders}
        description="Las siguientes modificaciones se aplicarán a los pedidos"
        onConfirm={handleModifyOrders}
        onCancel={() => toggleOpenConfirmModifidyOrders(false)}
        loadingConfirm={loadingModifyOrders}
        maxWidth="sm"
      >
        <DialogOrdersMultipeEditionPreviewModifications
          values={values}
          zoneOptions={zoneOptions}
        />
      </DialogConfimation>
    </>
  );
};

export default DialogOrdersMultipeEditionContent;
